// src/pages/MatchingHistory.js
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import styles from './MatchingHistory.module.css';
import MatchingResult from './MatchingResult';

// ── IMPORT DE REACT-PDF POUR LA GÉNÉRATION NATIVE ──
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';

import {
  Clock,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Inbox,
  Search,
  TrendingUp,
  Edit3,
  Check,
  MessageSquare,
  Star,
  Filter,
  X,
  Download,
  Send,
  MessageCircle,
  Loader2,
  FileText,
  Layers
} from 'lucide-react';

const formatDate = (isoDate) => {
  if (!isoDate) return 'Date inconnue';
  const now = new Date();
  const date = new Date(isoDate);
  const diffDays = Math.floor(Math.abs(now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0)
    return `Aujourd'hui à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  if (diffDays === 1)
    return `Hier à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const safeParse = (jsonString, fallback = null) => {
  try {
    return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString ?? fallback;
  } catch {
    return fallback;
  }
};

const getScoreVariant = (pct) => {
  if (pct >= 75) return 'high';
  if (pct >= 50) return 'medium';
  return 'low';
};

/* ─────────────────────────────────────────────────────────────
   STYLES DU PDF (Indépendant du CSS Web)
───────────────────────────────────────────────────────────── */
const pdfStyles = StyleSheet.create({
  page: { padding: 40, fontFamily: 'Helvetica', fontSize: 10, color: '#334155', lineHeight: 1.5, backgroundColor: '#ffffff' },
  header: { marginBottom: 20, textAlign: 'center', borderBottom: '2pt solid #0d4a68', paddingBottom: 10 },
  title: { fontSize: 22, fontWeight: 'bold', color: '#0d4a68', marginBottom: 5 },
  date: { fontSize: 10, color: '#64748b' },
  vsBox: { flexDirection: 'row', justifyContent: 'space-between', backgroundColor: '#f8fafc', padding: 15, borderRadius: 6, marginBottom: 20, border: '1pt solid #e2e8f0' },
  vsCol: { flex: 1 },
  vsLabel: { fontSize: 9, color: '#64748b', textTransform: 'uppercase', marginBottom: 3 },
  vsText: { fontSize: 12, fontWeight: 'bold', color: '#0f172a' },
  section: { marginBottom: 15 },
  sectionTitle: { fontSize: 12, fontWeight: 'bold', color: '#1e293b', backgroundColor: '#f1f5f9', padding: '6 8', borderRadius: 4, marginBottom: 8, textTransform: 'uppercase' },
  text: { marginBottom: 5 },
  textBold: { fontWeight: 'bold', color: '#0f172a' },
  scoreBox: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  scoreBadge: { backgroundColor: '#e0f2fe', color: '#0369a1', padding: '4 8', borderRadius: 4, fontSize: 14, fontWeight: 'bold', marginRight: 10 },
  verdictBadge: { fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase' },
  bulletRow: { flexDirection: 'row', marginBottom: 4, alignItems: 'flex-start' },
  bulletIcon: { width: 15, color: '#0ea5e9', fontWeight: 'bold', fontSize: 12 },
  bulletText: { flex: 1 },
  italic: { fontStyle: 'italic', color: '#64748b' }
});

/* ─────────────────────────────────────────────────────────────
   COMPOSANT DOCUMENT PDF (Génération vectorielle)
───────────────────────────────────────────────────────────── */
const MatchingPDFDocument = ({ data, note }) => {
  const scoringData = safeParse(data.scoring_details);
  const riskData = safeParse(data.risks_json);
  const salesData = safeParse(data.sales_json);

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        
        {/* En-tête */}
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.title}>Rapport d'Analyse Matching</Text>
          <Text style={pdfStyles.date}>Généré le {new Date().toLocaleDateString('fr-FR')} par CVtoSkill</Text>
        </View>

        {/* Fichiers comparés */}
        <View style={pdfStyles.vsBox}>
          <View style={pdfStyles.vsCol}>
            <Text style={pdfStyles.vsLabel}>Candidat</Text>
            <Text style={pdfStyles.vsText}>{data.file1_filename}</Text>
          </View>
          <View style={[pdfStyles.vsCol, { alignItems: 'flex-end' }]}>
            <Text style={pdfStyles.vsLabel}>Offre / Poste</Text>
            <Text style={pdfStyles.vsText}>{data.file2_filename}</Text>
          </View>
        </View>

        {/* Executive Summary */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Executive Summary</Text>
          <View style={pdfStyles.scoreBox}>
            <Text style={pdfStyles.scoreBadge}>Score : {data.global_score}%</Text>
            <Text style={pdfStyles.verdictBadge}>Recommandation : {data.verdict?.replace(/_/g, ' ') || 'N/A'}</Text>
          </View>
          <Text style={pdfStyles.text}>{data.summary || 'Pas de résumé disponible.'}</Text>
        </View>

        {/* Compétences Techniques */}
        {scoringData?.technical_skills?.must_have?.length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>Compétences Techniques</Text>
            {scoringData.technical_skills.must_have.map((sk, i) => (
              <View key={i} style={pdfStyles.bulletRow}>
                <Text style={pdfStyles.bulletIcon}>•</Text>
                <Text style={pdfStyles.bulletText}>
                  <Text style={pdfStyles.textBold}>{sk.skill}</Text> ({sk.level}) — {sk.status}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Analyse des Risques */}
        {riskData && Object.keys(riskData).length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>Analyse des Risques</Text>
            <Text style={pdfStyles.text}><Text style={pdfStyles.textBold}>Risque Global : </Text>{riskData.overall || 'N/A'}</Text>
            <Text style={pdfStyles.text}><Text style={pdfStyles.textBold}>Flight Risk : </Text>{riskData.flight_risk || 'N/A'}</Text>
          </View>
        )}

        {/* Stratégie Commerciale */}
        {salesData?.killer_arguments?.length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>Stratégie Commerciale</Text>
            {salesData.killer_arguments.map((arg, i) => (
              <View key={i} style={{ marginBottom: 8 }}>
                <View style={pdfStyles.bulletRow}>
                  <Text style={[pdfStyles.bulletIcon, { color: '#10b981' }]}>✓</Text>
                  <Text style={[pdfStyles.bulletText, pdfStyles.textBold]}>{arg.argument}</Text>
                </View>
                {arg.client_benefit && (
                  <View style={pdfStyles.bulletRow}>
                    <Text style={pdfStyles.bulletIcon}> </Text>
                    <Text style={[pdfStyles.bulletText, pdfStyles.italic]}>Bénéfice : {arg.client_benefit}</Text>
                  </View>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Notes Personnelles */}
        {(note || data.note) && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>Notes Personnelles</Text>
            <Text style={[pdfStyles.text, pdfStyles.italic]}>{note || data.note}</Text>
          </View>
        )}
      </Page>
    </Document>
  );
};

/* ─────────────────────────────────────────────────────────────
   HISTORY ITEM
───────────────────────────────────────────────────────────── */
const HistoryItem = ({ item, onNoteUpdate, onFavoriteToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState(item.note || '');
  const [saveStatus, setSaveStatus] = useState('idle');
  const [isFavorite, setIsFavorite] = useState(item.isFavorite);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  
  // États de chargement
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isWhatsappLoading, setIsWhatsappLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  const isNoteChanged = note !== (item.note || '');

  const resultData = {
    ...item,
    file1_filename: item.file1_filename || item.file1,
    file2_filename: item.file2_filename || item.file2,
    global_score: item.global_score || item.matching_percentage || item.percentage || 0,
    scoring_details: item.scoring_details,
    risks_json: item.risks_json,
    sales_json: item.sales_json,
    interview_json: item.interview_json,
    recruiter_tips_json: item.recruiter_tips_json,
    ai_transparency_json: item.ai_transparency_json,
  };

  const score = resultData.global_score;
  const variant = getScoreVariant(score);

  /* ── Actions ── */
  const handleSaveNote = async () => {
    setSaveStatus('saving');
    try {
      await api.put(`/matching/history/${item.id}/note`, { note });
      setSaveStatus('saved');
      onNoteUpdate(item.id, note);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleSendEmail = async (e) => {
    e.stopPropagation();
    const recipientEmail = window.prompt("Entrez l'adresse email du client :");
    if (!recipientEmail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      alert('Adresse email invalide.');
      return;
    }
    setIsSendingEmail(true);
    try {
      await api.post(`/matching/history/${item.id}/send-email`, { email: recipientEmail });
      alert(`Envoyé avec succès à ${recipientEmail} !`);
    } catch (err) {
      alert(err.response?.data?.error || "Erreur lors de l'envoi.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleWhatsApp = async (e) => {
    e.stopPropagation();
    setIsWhatsappLoading(true);
    try {
      const { data } = await api.get(`/matching/history/${item.id}/whatsapp-pitch`);
      if (navigator.share && navigator.canShare) {
        try { await navigator.share({ text: data.message }); setIsWhatsappLoading(false); return; }
        catch { /* fallback */ }
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(data.message)}`, '_blank');
    } catch {
      alert('Impossible de générer le message WhatsApp.');
    } finally {
      setIsWhatsappLoading(false);
    }
  };

  // NOUVELLE LOGIQUE DE TÉLÉCHARGEMENT PDF (react-pdf)
  const handleDownloadPDF = async (e) => {
    e.stopPropagation();
    setIsPdfLoading(true);

    try {
      // 1. Génération du Blob via @react-pdf/renderer
      const pdfBlob = await pdf(<MatchingPDFDocument data={resultData} note={note} />).toBlob();

      // 2. Création et exécution du téléchargement
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      
      const safeName = (resultData.file1_filename || 'candidat')
        .substring(0, 15)
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      link.download = `Matching_${safeName}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Erreur détaillée lors de la création du PDF :", err);
      alert('Erreur lors de la génération du PDF.');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    const next = !isFavorite;
    setIsTogglingFavorite(true);
    setIsFavorite(next);
    try {
      await api.put(`/matching/history/${item.id}/favorite`, { isFavorite: next });
      onFavoriteToggle(item.id, next);
    } catch {
      setIsFavorite(!next);
    }
    setIsTogglingFavorite(false);
  };

  return (
    <div className={`${styles.card} ${isOpen ? styles.cardOpen : ''} ${isFavorite ? styles.cardFavorite : ''}`}>

      {/* ── Header cliquable ── */}
      <div className={styles.cardHeader} onClick={() => setIsOpen(!isOpen)} role="button" tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}>

        {/* Score pill */}
        <div className={`${styles.scorePill} ${styles[variant]}`}>
          <span className={styles.scoreNum}>{score}%</span>
          <div className={styles.scoreTrack}>
            <div className={styles.scoreBar} style={{ width: `${score}%` }} />
          </div>
        </div>

        {/* Fichiers & résumé */}
        <div className={styles.cardInfo}>
          <div className={styles.fileRow}>
            <span className={styles.fileChip} title={resultData.file1_filename}>
              <FileText size={11} strokeWidth={2} />
              {resultData.file1_filename}
            </span>
            <span className={styles.vsLabel}>vs</span>
            <span className={styles.fileChip} title={resultData.file2_filename}>
              <FileText size={11} strokeWidth={2} />
              {resultData.file2_filename}
            </span>
          </div>
          {item.summary && (
            <p className={styles.summarySnippet}>
              {item.summary.substring(0, 120)}{item.summary.length > 120 ? '…' : ''}
            </p>
          )}
          <div className={styles.tagRow}>
            {item.verdict && (
              <span className={`${styles.verdictTag} ${item.verdict.includes('NO') ? styles.verdictRed : styles.verdictGreen}`}>
                {item.verdict.replace(/_/g, ' ')}
              </span>
            )}
            {item.note && !isOpen && (
              <span className={styles.noteSnippet}>
                <MessageSquare size={11} strokeWidth={2} />
                {item.note.substring(0, 40)}{item.note.length > 40 ? '…' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Meta + actions */}
        <div className={styles.cardSide}>
          <div className={styles.dateLabel}>
            <Clock size={12} strokeWidth={2} />
            {formatDate(item.created_at || item.date || item.Timestamp)}
          </div>

          <div className={styles.actions}>
            {/* Email */}
            <button className={`${styles.iconBtn} ${styles.emailBtn}`} onClick={handleSendEmail}
              disabled={isSendingEmail} title="Envoyer par email">
              {isSendingEmail
                ? <Loader2 size={15} strokeWidth={2} className={styles.spin} />
                : <Send size={15} strokeWidth={2} />}
            </button>

            {/* WhatsApp */}
            <button className={`${styles.iconBtn} ${styles.waBtn}`} onClick={handleWhatsApp}
              disabled={isWhatsappLoading} title="Pitch WhatsApp">
              {isWhatsappLoading
                ? <Loader2 size={15} strokeWidth={2} className={styles.spin} />
                : <MessageCircle size={15} strokeWidth={2} />}
            </button>

            {/* PDF - Modifié avec l'état de chargement */}
            <button className={styles.iconBtn} onClick={handleDownloadPDF} disabled={isPdfLoading} title="Télécharger PDF">
              {isPdfLoading 
                ? <Loader2 size={15} strokeWidth={2} className={styles.spin} /> 
                : <Download size={15} strokeWidth={2} />
              }
            </button>

            {/* Favori */}
            <button className={`${styles.iconBtn} ${styles.starBtn} ${isFavorite ? styles.starred : ''}`}
              onClick={handleToggleFavorite} disabled={isTogglingFavorite} title="Favori">
              <Star size={15} strokeWidth={2} />
            </button>

            {/* Expand */}
            <div className={styles.expandIcon}>
              {isOpen
                ? <ChevronUp size={16} strokeWidth={2} />
                : <ChevronDown size={16} strokeWidth={2} />}
            </div>
          </div>
        </div>
      </div>

      {/* ── Corps expanded ── */}
      {isOpen && (
        <div className={styles.cardBody}>
          <div className={styles.resultWrapper}>
            <MatchingResult data={resultData} />
          </div>

          {/* Notes */}
          <div className={styles.notePanel}>
            <div className={styles.notePanelHeader}>
              <Edit3 size={15} strokeWidth={2} />
              <span>Notes personnelles</span>
            </div>
            <textarea
              className={styles.noteArea}
              placeholder="Ajoutez vos commentaires…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
            <div className={styles.noteFooter}>
              <span className={styles.charCount}>{note.length} car.</span>
              <button
                className={`${styles.saveBtn} ${saveStatus !== 'idle' ? styles[saveStatus] : ''}`}
                onClick={handleSaveNote}
                disabled={!isNoteChanged || saveStatus === 'saving'}
              >
                {saveStatus === 'saving' && <Loader2 size={14} strokeWidth={2} className={styles.spin} />}
                {saveStatus === 'saved' && <Check size={14} strokeWidth={2.5} />}
                {saveStatus === 'error' && 'Erreur'}
                {saveStatus === 'idle' && (isNoteChanged ? 'Enregistrer' : 'Sauvegardé')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MATCHING HISTORY (parent)
───────────────────────────────────────────────────────────── */
const MatchingHistory = ({ refreshKey }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour la pagination et le backend
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Délai (debounce) pour la recherche (évite de spammer l'API quand on tape)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Retour page 1 si on fait une nouvelle recherche
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Si on change les autres filtres, on revient à la page 1
  useEffect(() => {
    setPage(1);
  }, [minScore, showFavoritesOnly]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/matching/history', {
        params: {
          page: page,
          per_page: 10,
          search: debouncedSearch,
          min_score: minScore,
          favorites_only: showFavoritesOnly
        }
      });
      setHistory(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total || 0);
    } catch {
      setError("Impossible de charger l'historique.");
    }
    setLoading(false);
  };

  // Recharger quand un paramètre change ou via le refreshKey
  useEffect(() => { 
    loadHistory(); 
  }, [refreshKey, page, debouncedSearch, minScore, showFavoritesOnly]);

  const handleNoteUpdate = (id, newNote) =>
    setHistory((prev) => prev.map((item) => item.id === id ? { ...item, note: newNote } : item));

  const handleFavoriteToggle = (id, val) =>
    setHistory((prev) => prev.map((item) => item.id === id ? { ...item, isFavorite: val } : item));

  const activeFilters = minScore > 0 || showFavoritesOnly || debouncedSearch !== '';

  return (
    <div className={styles.container}>

      {/* ── En-tête ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Historique des analyses</h2>
          <p className={styles.subtitle}>Retrouvez et comparez tous vos matchings précédents.</p>
        </div>

        {totalItems > 0 && (
          <div className={styles.statsRow}>
            <div className={styles.statChip}>
              <Layers size={12} strokeWidth={2} />
              <span className={styles.statVal}>{totalItems}</span>
              <span className={styles.statLabel}>Analyses totales</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Barre de recherche & filtres ── */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Search size={15} strokeWidth={2} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Rechercher par fichier, résumé, notes…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className={styles.clearBtn} onClick={() => setSearchTerm('')}>
              <X size={13} strokeWidth={2.5} />
            </button>
          )}
        </div>

        <div className={styles.filterWrap}>
          <button
            className={`${styles.filterBtn} ${isFilterOpen || activeFilters ? styles.filterBtnActive : ''}`}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <Filter size={14} strokeWidth={2} />
            Filtres
            {activeFilters && <span className={styles.filterDot} />}
          </button>

          {isFilterOpen && (
            <div className={styles.filterPanel}>
              <p className={styles.filterPanelTitle}>Affiner les résultats</p>

              <div className={styles.filterField}>
                <label className={styles.filterLabel}>
                  <TrendingUp size={13} strokeWidth={2} />
                  Score minimum — <strong>{minScore}%</strong>
                </label>
                <input
                  type="range" min="0" max="100" step="5"
                  value={minScore}
                  onChange={(e) => setMinScore(Number(e.target.value))}
                  className={styles.rangeInput}
                />
                <div className={styles.rangeLabels}>
                  <span>0%</span><span>100%</span>
                </div>
              </div>

              <label className={styles.checkboxLabel}>
                <div className={`${styles.checkboxBox} ${showFavoritesOnly ? styles.checkboxChecked : ''}`}>
                  {showFavoritesOnly && <Check size={11} strokeWidth={3} />}
                </div>
                <input type="checkbox" hidden checked={showFavoritesOnly}
                  onChange={(e) => setShowFavoritesOnly(e.target.checked)} />
                <Star size={13} strokeWidth={2} />
                Favoris uniquement
              </label>

              {activeFilters && (
                <button className={styles.resetBtn}
                  onClick={() => { setMinScore(0); setShowFavoritesOnly(false); setSearchTerm(''); }}>
                  <X size={12} strokeWidth={2.5} /> Réinitialiser
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── États ── */}
      {loading && (
        <div className={styles.stateBox}>
          <div className={styles.loaderRing}>
            <Loader2 size={28} strokeWidth={2} className={styles.spin} />
          </div>
          <p>Chargement de l'historique…</p>
        </div>
      )}

      {!loading && error && (
        <div className={styles.stateBox}>
          <AlertCircle size={40} strokeWidth={1.5} className={styles.stateIconError} />
          <h3>Erreur de chargement</h3>
          <p>{error}</p>
          <button className={styles.retryBtn} onClick={loadHistory}>Réessayer</button>
        </div>
      )}

      {!loading && !error && history.length === 0 && (
        <div className={styles.stateBox}>
          <Inbox size={44} strokeWidth={1.2} className={styles.stateIconEmpty} />
          <h3>{totalItems === 0 && !activeFilters ? 'Aucune analyse' : 'Aucun résultat'}</h3>
          <p>
            {totalItems === 0 && !activeFilters
              ? 'Lancez votre premier matching pour voir les résultats ici.'
              : 'Modifiez vos filtres ou votre recherche pour trouver des résultats.'}
          </p>
        </div>
      )}

      {/* ── Liste ── */}
      {!loading && !error && history.length > 0 && (
        <>
          <div className={styles.list}>
            {history.map((item) => (
              <HistoryItem
                key={item.id}
                item={item}
                onNoteUpdate={handleNoteUpdate}
                onFavoriteToggle={handleFavoriteToggle}
              />
            ))}
          </div>
          
          {/* PAGINATION */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '20px', alignItems: 'center' }}>
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))} 
                disabled={page === 1}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: page === 1 ? '#f8fafc' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                Précédent
              </button>
              <span style={{ fontSize: '14px', color: '#64748b' }}>Page {page} sur {totalPages}</span>
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                disabled={page === totalPages}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: page === totalPages ? '#f8fafc' : '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MatchingHistory;